import { ActivityIndicator, StyleSheet, View } from 'react-native';
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  Clock3,
  HelpCircle,
  RefreshCw,
  Search,
  UploadCloud,
} from 'lucide-react-native';
import type { PhotoSearchProcessingStep } from '../../types/photoSearch';
import { tokens } from '../../theme/tokens';
import { AppButton } from '../ui/AppButton';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';

type PhotoSearchProcessingStateProps = {
  step: PhotoSearchProcessingStep;
  message: string;
  onRetry?: () => void;
  onReset?: () => void;
};

const stepConfig = {
  idle: { label: '等待题图', icon: Camera, tone: tokens.state.idle },
  permission_denied: { label: '需要授权', icon: AlertCircle, tone: tokens.colors.warning },
  uploading: { label: '正在上传题图', icon: UploadCloud, tone: tokens.state.uploading },
  recognizing: { label: '正在识别题目', icon: Search, tone: tokens.state.recognizing },
  matching: { label: '正在确认题目', icon: Search, tone: tokens.state.matching },
  solving: { label: '正在生成讲解', icon: CheckCircle2, tone: tokens.state.solving },
  low_confidence: { label: '识别置信度偏低', icon: HelpCircle, tone: tokens.state.lowConfidence },
  no_match: { label: '未匹配到题库', icon: Search, tone: tokens.state.noMatch },
  agent_timeout: { label: 'AI 讲解超时', icon: Clock3, tone: tokens.state.agentTimeout },
  retrying: { label: '正在重试', icon: RefreshCw, tone: tokens.state.retrying },
  success_handoff: { label: '已进入讲解', icon: CheckCircle2, tone: tokens.state.successHandoff },
  failed: { label: '处理失败', icon: AlertCircle, tone: tokens.state.failed },
} as const;

const busySteps: PhotoSearchProcessingStep[] = [
  'uploading',
  'recognizing',
  'matching',
  'solving',
  'retrying',
];

const retryableSteps: PhotoSearchProcessingStep[] = [
  'failed',
  'permission_denied',
  'low_confidence',
  'no_match',
  'agent_timeout',
];

export function PhotoSearchProcessingState({
  step,
  message,
  onRetry,
  onReset,
}: PhotoSearchProcessingStateProps) {
  const config = stepConfig[step];
  const Icon = config.icon;
  const isBusy = busySteps.includes(step);
  const canRetry = retryableSteps.includes(step);

  return (
    <AppCard variant={step === 'success_handoff' ? 'muted' : 'default'} style={styles.card}>
      <View style={[styles.iconWrap, { backgroundColor: `${config.tone}1F` }]}>
        {isBusy ? <ActivityIndicator color={config.tone} /> : <Icon size={24} color={config.tone} />}
      </View>
      <View style={styles.copy}>
        <AppText variant="subtitle" weight="bold" align="center">
          {config.label}
        </AppText>
        <AppText variant="body" tone="muted" align="center">
          {message}
        </AppText>
      </View>
      {canRetry ? (
        <View style={styles.actionRow}>
          {onReset ? (
            <AppButton
              label="重新选择"
              variant="secondary"
              onPress={onReset}
              style={styles.actionButton}
            />
          ) : null}
          {onRetry ? <AppButton label="重试" onPress={onRetry} style={styles.actionButton} /> : null}
        </View>
      ) : null}
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: {
    alignItems: 'center',
    gap: tokens.spacing.lg,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  copy: {
    gap: tokens.spacing.sm,
  },
  actionRow: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
    alignSelf: 'stretch',
  },
  actionButton: {
    flex: 1,
  },
});
